import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';
import * as yup from 'yup';
import { useFormik, FormikValues } from 'formik';
import { Spacing } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { Modal } from '@mui/material';
import { Title, Text, CenteredColumn, Button } from '@/shared/ui';
import { useRegistration } from '@/context/RegistrationContext';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { useEligibilitySubmissionFlow } from '@/hooks/useEligibilitySubmissionFlow';
import { useAnalytics, useReCaptcha } from '@/hooks';
import { PhaseProgressBar } from '@/components/PhaseProgressBar';
import { phaseForRoute } from '@/consts/phases';
import { VIEW_COVERAGE, CONTINUE_WEBREG } from '@/consts/events';
import { ENCRYPTED_BUTTON_PRESSED_MEDICARE_CONTINUE } from '@/consts/encryptedEvents';
import { useEncryptedEventsExternal } from '@/hooks/useEncryptedEventsExternal';
import {
  useFormValues,
  normalizeMbi,
  FIRST_NAME_KEY,
  LAST_NAME_KEY,
  DATE_OF_BIRTH_KEY,
  MBI_KEY,
  GENDER_KEY,
  EMAIL_KEY,
  PHONE_KEY,
} from '@/hooks/useFormValues';
import {
  RgFieldRow,
  RgTextField,
  RgSelectField,
  RgDob,
  RgLink,
  RgBody,
  RgFoot,
} from '@/shared/borisFields';
import type { CmsGender } from '@/types/registration';
import medicareCardMockup from '@/assets/images/medicare-card-mockup.svg';
import CloseCross from '@/assets/images/close-cross.svg';

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  gap: ${Spacing.x1_5}px;
`;

const ComplianceText = styled(Text)`
  color: ${BorisScreener.inkSoft};
`;

// Boris inline disclosure note (register.css .rg-disclosure): lime card + ink "i".
const DisclosureNotice = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background-color: ${BorisScreener.lime};
  border: 1px solid rgba(28, 31, 33, 0.07);
  border-radius: 15px;
  padding: 15px 17px;
  text-align: left;
  width: 100%;
`;

const DisclosureIcon = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${BorisScreener.ink};
  color: ${BorisScreener.lime};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 15px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
`;

const Cta = styled.button`
  align-self: flex-start;
  width: 100%;
  max-width: 340px;
  height: 72px;
  border-radius: 32px;
  border: none;
  background: ${BorisScreener.btnPrimary};
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.16s ease;

  &:hover:not(:disabled) {
    background: ${BorisScreener.btnPrimaryHover};
  }
  &:disabled {
    background: #a9b6ae;
    cursor: default;
  }
`;

// Boris `.rg-screen`: fills the frame's main column (flex:1/min-height:0
// against Layout's FlowMain) and splits into RgBody (scrolls) + RgFoot (pinned
// flex sibling); see borisFields.tsx for why that split replaces the retired
// `position:sticky` foot.
const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

// Boris "How to find it" popup (register.css .rg-modal): cream card, 340px,
// X-circle top-right, 23px semibold title, inkSoft body, Medicare-card wireframe.
const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 48px);
  max-width: 340px;
  background: ${BorisScreener.sand};
  border-radius: 26px;
  padding: 26px 24px 24px;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  gap: 14px;
  outline: none;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: rgba(28, 31, 33, 0.06);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  padding-right: 28px;
  font-size: 23px;
  font-weight: 600;
  line-height: 1.18;
  letter-spacing: -0.01em;
  color: ${BorisScreener.ink};
`;

const ModalBody = styled.p`
  margin: 0;
  font-size: 15.5px;
  line-height: 1.45;
  color: ${BorisScreener.inkSoft};
`;

const ModalCardImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
`;

const MEDICARE_FIELDS = [
  FIRST_NAME_KEY,
  LAST_NAME_KEY,
  DATE_OF_BIRTH_KEY,
  MBI_KEY,
  GENDER_KEY,
  EMAIL_KEY,
  PHONE_KEY,
];

const MedicareCoverageStep: React.FC = () => {
  const { t } = useTranslation();
  const { updateMedicareCoverage, updateAccount, registrationData } = useRegistration();
  const { trackView, track } = useAnalytics();
  const { trackEncryptedEvent } = useEncryptedEventsExternal();
  const { executeReCaptcha } = useReCaptcha({ screen: VIEW_COVERAGE });
  // Called for its accessibility/back-nav side effects; RND-36523 replaces the
  // linear navigateToNextStep with the up-front CMS eligibility submission below.
  useAppFlow(STEP_KEYS.MEDICARE_COVERAGE);
  const { submitAndRoute, isSubmitting } = useEligibilitySubmissionFlow();
  const [mbiModalOpen, setMbiModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const fieldConfig = useFormValues(MEDICARE_FIELDS);
  const genderOptions = fieldConfig[GENDER_KEY]?.selectOptionEntries ?? [];

  const validationSchema = useMemo(
    () =>
      yup.object(
        Object.fromEntries(
          Object.entries(fieldConfig).map(([key, value]) => [key, value.validationSchema]),
        ),
      ),
    [fieldConfig],
  );

  useEffect(() => {
    trackView({ event: VIEW_COVERAGE });
  }, [trackView]);

  const handleSubmit = async (values: FormikValues) => {
    const normalizedMbi = normalizeMbi(values[MBI_KEY]);

    track({ event: CONTINUE_WEBREG, properties: { source: 'coverage_access' } });
    trackEncryptedEvent({
      event: ENCRYPTED_BUTTON_PRESSED_MEDICARE_CONTINUE,
      properties: {
        firstName: values[FIRST_NAME_KEY],
        lastName: values[LAST_NAME_KEY],
        dateOfBirth: values[DATE_OF_BIRTH_KEY],
        mbi: normalizedMbi,
      },
    });

    setSubmitError(false);
    try {
      await executeReCaptcha('medicare_coverage_submit');
      updateMedicareCoverage({
        firstName: values[FIRST_NAME_KEY],
        lastName: values[LAST_NAME_KEY],
        dateOfBirth: values[DATE_OF_BIRTH_KEY],
        mbi: normalizedMbi,
        gender: values[GENDER_KEY] as CmsGender,
      });
      // Email + phone are captured on this identity screen per PRD 01 6.1 / Boris;
      // stored on the account object (create-account later only sets the password).
      updateAccount({
        email: values[EMAIL_KEY],
        phoneNumber: values[PHONE_KEY],
      });
      // RND-36523: fire the CMS $check-eligibility call for all 3 candidate tracks
      // up front, then route to the polling result screen. Pass the fresh Formik
      // values (not context) so the submission never reads a not-yet-committed
      // context on this same tick.
      const { retry } = await submitAndRoute({
        mbi: normalizedMbi,
        firstName: values[FIRST_NAME_KEY],
        lastName: values[LAST_NAME_KEY],
        dateOfBirth: values[DATE_OF_BIRTH_KEY],
        gender: values[GENDER_KEY] as CmsGender,
      });
      // Every CMS submission failed — stay put and surface a retry.
      if (retry) {
        setSubmitError(true);
      }
    } catch (error) {
      console.error('medicare coverage submit failed:', error);
      setSubmitError(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      [FIRST_NAME_KEY]: '',
      [LAST_NAME_KEY]: '',
      [DATE_OF_BIRTH_KEY]: '',
      [MBI_KEY]: '',
      [GENDER_KEY]: registrationData.medicareCoverage.gender ?? '',
      [EMAIL_KEY]: registrationData.account.email ?? '',
      [PHONE_KEY]: registrationData.account.phoneNumber ?? '',
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: handleSubmit,
  });

  // Mirror the app's "don't show a required error until the field has content" rule.
  const errFor = (key: string): string | undefined => {
    const raw = ((formik.values as Record<string, string>)[key] ?? '').toString();
    const content = key === DATE_OF_BIRTH_KEY ? raw.replace(/\D/g, '') : raw.trim();
    const touched = formik.touched as Record<string, boolean | undefined>;
    const errors = formik.errors as Record<string, string | undefined>;
    return touched[key] && errors[key] && content.length > 0 ? String(errors[key]) : undefined;
  };

  return (
    <Form onSubmit={formik.handleSubmit} noValidate>
      <RgBody>
        <PhaseProgressBar phase={phaseForRoute('/medicare-coverage')} />
        <CenteredColumn span={12}>
          <HeadingContainer>
            <Title size="h2">{t('medicare-coverage.title')}</Title>
            <Text size="p1">{t('medicare-coverage.subtitle')}</Text>
            <ComplianceText size="p2">
              <Trans i18nKey="medicare-coverage.description" components={{ 1: <Text size="p2" bold /> }} />
            </ComplianceText>
            <ComplianceText size="p2">{t('medicare-coverage.disclaimer')}</ComplianceText>
          </HeadingContainer>
        </CenteredColumn>

        <Fields>
          <RgFieldRow>
            <RgTextField
              name={FIRST_NAME_KEY}
              label={t('medicare-coverage.first-name')}
              value={formik.values[FIRST_NAME_KEY]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="given-name"
              error={errFor(FIRST_NAME_KEY)}
            />
            <RgTextField
              name={LAST_NAME_KEY}
              label={t('medicare-coverage.last-name')}
              value={formik.values[LAST_NAME_KEY]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="family-name"
              error={errFor(LAST_NAME_KEY)}
            />
          </RgFieldRow>

          <RgDob
            label={t('medicare-coverage.date-of-birth')}
            value={formik.values[DATE_OF_BIRTH_KEY]}
            onChange={(assembled) => formik.setFieldValue(DATE_OF_BIRTH_KEY, assembled)}
            onBlur={() => formik.setFieldTouched(DATE_OF_BIRTH_KEY, true)}
            error={errFor(DATE_OF_BIRTH_KEY)}
          />

          <RgSelectField
            name={GENDER_KEY}
            label={t('medicare-coverage.gender')}
            value={formik.values[GENDER_KEY]}
            placeholder={t('general.select')}
            options={genderOptions}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={errFor(GENDER_KEY)}
          />

          <RgTextField
            name={MBI_KEY}
            label={t('medicare-coverage.mbi')}
            value={formik.values[MBI_KEY]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            mono
            widthCh={18.5}
            placeholder="1EG4-TE5-MK73"
            error={errFor(MBI_KEY)}
            hint={t('medicare-coverage.mbi-hint')}
            rightLabel={
              <RgLink type="button" onClick={() => setMbiModalOpen(true)}>
                {t('medicare-coverage.mbi-how-to')}
              </RgLink>
            }
          />

          <RgTextField
            name={EMAIL_KEY}
            label={t('medicare-coverage.email')}
            value={formik.values[EMAIL_KEY]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            type="email"
            inputMode="email"
            autoComplete="email"
            error={errFor(EMAIL_KEY)}
          />

          <RgTextField
            name={PHONE_KEY}
            label={t('medicare-coverage.phone')}
            value={formik.values[PHONE_KEY]}
            onChange={(e) =>
              formik.setFieldValue(PHONE_KEY, e.target.value.replace(/\D/g, '').slice(0, 10))
            }
            onBlur={formik.handleBlur}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            error={errFor(PHONE_KEY)}
          />
        </Fields>

        <DisclosureNotice>
          <DisclosureIcon aria-hidden="true">i</DisclosureIcon>
          <Text size="p2">{t('medicare-coverage.disclosure')}</Text>
        </DisclosureNotice>
      </RgBody>

      <RgFoot>
        {/* WCAG 4.1.3: eligibility-submission error announced to assistive tech.
            Mounted only on error so role="alert" announces on insertion and does
            not add a second, empty alert region alongside field validation. */}
        {submitError && (
          <div role="alert">
            <ComplianceText size="p2">{t('eligibility-result.error')}</ComplianceText>
          </div>
        )}
        <Cta
          type="submit"
          disabled={!formik.isValid || isSubmitting}
          aria-label={t('medicare-coverage.cta')}
        >
          {t('medicare-coverage.cta')}
        </Cta>
      </RgFoot>

      <Modal
        open={mbiModalOpen}
        onClose={() => setMbiModalOpen(false)}
        aria-labelledby="mbi-help-title"
      >
        <ModalCard>
          <ModalClose
            type="button"
            onClick={() => setMbiModalOpen(false)}
            aria-label={t('medicare-coverage.mbi-modal-close')}
          >
            <img src={CloseCross} alt="" />
          </ModalClose>
          <ModalTitle id="mbi-help-title">{t('medicare-coverage.mbi-modal-title')}</ModalTitle>
          <ModalBody>{t('medicare-coverage.mbi-modal-body')}</ModalBody>
          <ModalCardImg src={medicareCardMockup} alt={t('medicare-coverage.mbi-modal-img-alt')} />
          <Button
            text={t('medicare-coverage.mbi-modal-cta')}
            onClick={() => setMbiModalOpen(false)}
          />
        </ModalCard>
      </Modal>
    </Form>
  );
};

export { MedicareCoverageStep };
